/**
 * H3 · প্রস্তুত ঝুড়ি — `/explore/2/grocery` (audit finding 09)
 *
 * Transcribed from `design/Oprappo P0 Screens.dc.html`, frame H3 (360 × 680).
 *
 * Frame note: "Two baskets reach a filled cart in one tap, taking the first grocery
 * order from seven taps to three. Removing from a familiar list is easier than
 * assembling one, which is the whole reason this works."
 *
 * It lives in WP-D's `cart` directory because it is the basket→cart door and its footer
 * is the cart footer; the route itself is the one docs/11 gives.
 *
 * ── WIRED ────────────────────────────────────────────────────────────────────
 * `GET /v1/baskets?category_id=grocery` returns the baskets, the category pills AND the
 * cart summary — the footer here IS the cart's footer, so it is answered in the same
 * round trip rather than by a second call that would draw it twice. `কার্টে দিন` is
 * `POST /v1/cart/basket`, which is docs/05 § baskets' own route: one tap fills the cart.
 *
 * KNOWN DEFECT 2 — the empty-cart amount. The footer draws `৳ 0` in `color:#8B9795`,
 * which docs/01 and docs/02 both reserve for borders and SVG strokes ("greyBorder as a
 * text colour is a bug"). Built as drawn, per docs/01 § "when the design looks wrong":
 * flag it and build it as designed. `tools/visual-regression/token-lint.mjs` already
 * reports the frame itself at `Oprappo P0 Screens.dc.html:2334`, and will report this
 * screen's built CSS for the same reason. It is not silently changed and not silently
 * kept — it is in the WP-D report as a decision that is owed.
 *
 * A second, smaller thing on the same line: `৳ 0` is drawn in `'Anek Bangla'`, while
 * docs/02 § numerals puts Latin figures in Manrope with tabular-nums. Transcribed as
 * drawn and reported.
 */
import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Chip, IconButton } from '@oprappo/ui';
import type { BasketsResponse } from '@oprappo/contracts';
import { Frame, SAND_FRAME } from '../offering/chrome.js';
import { Loaded, commerce, useResource } from '../offering/commerce.js';
import { createSingleFlight } from './writes.js';

/** The category H3 is drawn for. docs/11 gives the route; this is its id in the API. */
export const GROCERY_CATEGORY_ID = 'grocery';

export function H3GroceryView({
  baskets,
  onAdd,
}: {
  baskets: BasketsResponse;
  onAdd?: (basketId: string) => void;
}): JSX.Element {
  const navigate = useNavigate();

  return (
    <Frame className={SAND_FRAME}>
      <div className="px-[16px] pt-[16px]">
        <div className="flex gap-[12px] items-center mb-[13px]">
          <IconButton control="back" size={20} onClick={() => navigate(-1)} />
          <div className="font-anek font-semibold text-[20px] leading-[1.75] text-ink">
            {baskets.title_bn}
          </div>
        </div>
      </div>

      <div className="flex-1 px-[16px] oprappo-scroll">
        <div className="font-anek font-semibold text-[15px] leading-[1.75] text-muted mb-[11px]">
          {baskets.baskets_heading_bn}
        </div>

        <div className="flex flex-col gap-[10px]">
          {baskets.baskets.map((b) => (
            <div
              key={b.id}
              className={
                'bg-white border border-border px-[14px] py-[13px]' +
                (b.emphasised ? ' border-l-[3px] border-l-teal' : '')
              }
            >
              <div className="flex justify-between items-start gap-[11px]">
                <div className="flex-1">
                  <div className="font-anek font-semibold text-[16px] leading-[1.75] text-ink">
                    {b.title_bn}
                  </div>
                  <div className="font-anek font-normal text-[13.5px] leading-[1.75] text-muted mt-[2px]">
                    {b.contents_bn}
                  </div>
                </div>
                <div className="font-manrope font-semibold text-[16px] leading-[1] text-ink tabular-nums flex-none">
                  {b.price.display}
                </div>
              </div>
              {/*
               * Drawn `10px 15px` (primary) and `10px 14px` (secondary). § 06's inline
               * Button is `10px 14px`, so the secondary matches exactly and the primary
               * is a pixel narrower each side. The component is used and the pixel is
               * reported rather than a second button being written.
               */}
              <div className="flex gap-[9px] mt-[12px] pt-[11px] border-t border-border">
                <Button size="inline" block={false} onClick={() => onAdd?.(b.id)}>
                  {baskets.add_label_bn}
                </Button>
                <Button size="inline" tone="secondary" block={false}>
                  {baskets.contents_label_bn}
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="font-anek font-normal text-[13px] leading-[1.75] text-body mt-[13px]">
          {baskets.note_bn}
        </div>

        <div className="border-t border-border mt-[16px] pt-[14px]">
          <div className="font-anek font-semibold text-[15px] leading-[1.75] text-muted mb-[11px]">
            {baskets.or_heading_bn}
          </div>
          <div className="flex flex-wrap gap-[8px]">
            {baskets.categories.map((c) => (
              // The frame draws these pills on `background:#fff`; § 03's Chip is
              // transparent, because the design system draws it on a white surface.
              // The white ground is supplied behind the chip rather than fought for
              // with a competing utility class. Reported.
              <span key={c.id} className="bg-white rounded-pill inline-flex flex-none">
                <Chip labelBn={c.label_bn} />
              </span>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white border-t border-border shadow-bottombar px-[16px] py-[12px] flex justify-between items-center gap-[11px]">
        {/*
         * `কার্ট খালি` when the cart is empty and `পণ্যের দাম` when it is not — both
         * drawn, both this same footer, and WHICH ONE is a fact about the cart that the
         * server holds rather than a branch this screen takes.
         */}
        <div className="font-anek font-normal text-[14px] leading-[1.75] text-muted">
          {baskets.cart.label_bn}
        </div>
        {/* #8B9795 as text, and Anek rather than Manrope — both as drawn, both flagged. */}
        <div className="font-anek font-medium text-[14px] leading-[1.75] text-muted flex-none">
          {baskets.cart.amount.display}
        </div>
      </div>
    </Frame>
  );
}

export function H3Grocery(): JSX.Element {
  const navigate = useNavigate();
  const load = useCallback(
    (signal: AbortSignal) => commerce.baskets(GROCERY_CATEGORY_ID, signal),
    [],
  );
  const [resource] = useResource(load, `baskets:${GROCERY_CATEGORY_ID}`);

  /**
   * One tap fills the cart, and the cart is where it lands.
   *
   * The navigation waits for the write: arriving at D3 before the basket is in it would
   * show an empty cart and then a full one, which reads as a bug on the screen whose
   * whole promise is that one tap was enough.
   *
   * ── WHY THE SECOND TAP IS DROPPED ────────────────────────────────────────────
   * `POST /v1/cart/basket` is an ADD, and adding is not idempotent: two taps on কার্টে দিন
   * before the first answers put the basket in the cart twice, and the customer finds out
   * at the footer. The route carries no `Idempotency-Key` — `idempotent()` wraps only
   * `POST /v1/orders` — and sending one here would claim a guarantee the server has not
   * made, so the duplicate is refused where it starts. The flag is never lowered on
   * success on purpose: success navigates away, and a screen that is leaving should not
   * accept another basket on the way out.
   */
  // The gate must outlive a render, so it is held in a ref and built once. `navigate` is
  // read through a ref of its own rather than captured: a callback built on the first
  // render would keep that render's `navigate` for the life of the screen.
  const go = useRef(navigate);
  go.current = navigate;
  const gate = useRef<(basketId: string) => void>();
  gate.current ??= createSingleFlight(async (basketId: string) => {
    await commerce.addBasket(basketId);
    go.current('/cart');
  });
  const add = gate.current;

  return (
    <Loaded resource={resource}>
      {(baskets) => <H3GroceryView baskets={baskets} onAdd={add} />}
    </Loaded>
  );
}
